import { z } from "zod";

const segmentInputSchema = z
  .object({
    segmentNumber: z.number().int().positive(),
    title: z.string().min(1),
    description: z.string().optional(),
    startPage: z.number().int().positive().optional(),
    endPage: z.number().int().positive().optional(),
    startChapter: z.number().int().positive().optional(),
    endChapter: z.number().int().positive().optional(),
  })
  .refine(
    (s) =>
      (s.startPage !== undefined && s.endPage !== undefined) ||
      (s.startChapter !== undefined && s.endChapter !== undefined),
    "Each segment requires either a page range or a chapter range",
  )
  .refine(
    (s) =>
      s.startPage === undefined ||
      s.endPage === undefined ||
      s.endPage >= s.startPage,
    "endPage must be greater than or equal to startPage",
  )
  .refine(
    (s) =>
      s.startChapter === undefined ||
      s.endChapter === undefined ||
      s.endChapter >= s.startChapter,
    "endChapter must be greater than or equal to startChapter",
  );

/**
 * Book reference for system room creation:
 * either an existing book id, or a "new" spec that creates the book on demand.
 */
const bookRefSchema = z.union([
  z.object({ id: z.string().min(1, "Book id required") }),
  z.object({
    title: z.string().min(1, "Book title required"),
    author: z.string().min(1, "Book author required"),
    description: z.string().optional(),
    coverUrl: z.string().url().optional(),
  }),
]);

export const createSystemRoomSchema = z
  .object({
    /** Book reference (existing id OR new spec for create-on-demand) */
    book: bookRefSchema,
    name: z.string().min(1, "Name is required").max(200),
    startDate: z.string().datetime("Invalid start date"),
    endDate: z.string().datetime("Invalid end date"),
    maxMembers: z.number().int().positive("Max members must be positive"),
    hookLine: z
      .string()
      .max(280, "Hook line must be 280 characters or less")
      .optional(),
    /** Segment schedule: must be exactly 4 contiguous weeks */
    segments: z
      .array(segmentInputSchema)
      .length(4, "System rooms require exactly 4 segments (one per week)"),
  })
  .refine(
    (data) => {
      const numbers = data.segments
        .map((s) => s.segmentNumber)
        .sort((a, b) => a - b);
      return numbers.every((n, i) => n === i + 1);
    },
    {
      message: "Segments must be numbered 1, 2, 3, 4 (one per week)",
      path: ["segments"],
    },
  )
  .refine(
    (data) => {
      const sorted = [...data.segments].sort(
        (a, b) => a.segmentNumber - b.segmentNumber,
      );
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];
        if (
          prev.endPage !== undefined &&
          curr.startPage !== undefined &&
          curr.startPage < prev.endPage
        )
          return false;
        if (
          prev.endChapter !== undefined &&
          curr.startChapter !== undefined &&
          curr.startChapter < prev.endChapter
        )
          return false;
      }
      return true;
    },
    {
      message: "Segment ranges must not overlap",
      path: ["segments"],
    },
  );

export type CreateSystemRoomInput = z.infer<typeof createSystemRoomSchema>;

/**
 * Admin create room — schema used by the operator console and mobile admin
 * dashboard. Accepts either an existing book ID or a book spec (title +
 * author) for create-on-demand. If `segments` is omitted, the server
 * auto-suggests them with Margaux. If provided, it must be exactly 4
 * contiguously-numbered segments.
 */
export const adminCreateRoomSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(200),
    book: bookRefSchema,
    startDate: z.string().datetime("Invalid start date"),
    endDate: z.string().datetime("Invalid end date"),
    maxMembers: z
      .number()
      .int()
      .positive("Max members must be positive")
      .optional(),
    hookLine: z
      .string()
      .max(280, "Hook line must be 280 characters or less")
      .optional(),
    segments: z.array(segmentInputSchema).length(4).optional(),
  })
  .refine((data) => new Date(data.endDate) > new Date(data.startDate), {
    message: "End date must be after start date",
    path: ["endDate"],
  })
  .refine(
    (data) => {
      if (!data.segments) return true;
      const numbers = data.segments
        .map((s) => s.segmentNumber)
        .sort((a, b) => a - b);
      return numbers.every((n, i) => n === i + 1);
    },
    {
      message: "Segments must be numbered 1, 2, 3, 4 (one per week)",
      path: ["segments"],
    },
  );

export type AdminCreateRoomInput = z.infer<typeof adminCreateRoomSchema>;

/**
 * Admin Margaux preview — runs `generateIntro` + `generateHookLine` for the
 * given book reference so the operator can preview the AI output before
 * committing a room.
 */
export const adminPreviewMargauxSchema = z.object({
  book: bookRefSchema,
});

export type AdminPreviewMargauxInput = z.infer<
  typeof adminPreviewMargauxSchema
>;

/**
 * Admin parse-yaml — accepts a YAML string and returns the first room as
 * `AdminCreateRoomInput`. The web operator console uses this for "paste YAML"
 * mode, so the same parser used by the CLI is the source of truth.
 */
export const adminParseRoomYamlSchema = z.object({
  yaml: z.string().min(1, "YAML body is required"),
});

export type AdminParseRoomYamlInput = z.infer<typeof adminParseRoomYamlSchema>;

export const createBookProposalSchema = z.object({
  bookTitle: z.string().min(1, "Book title is required").max(300),
  bookAuthor: z.string().max(200).optional(),
  reason: z.string().max(1000).optional(),
});

export type CreateBookProposalInput = z.infer<typeof createBookProposalSchema>;

export const updateReadingProgressSchema = z
  .object({
    currentPage: z
      .number()
      .int()
      .min(0, "Page number must be non-negative")
      .optional(),
    percentComplete: z
      .number()
      .min(0, "Percent must be at least 0")
      .max(100, "Percent must be at most 100")
      .optional(),
  })
  .refine(
    (data) =>
      data.currentPage !== undefined || data.percentComplete !== undefined,
    "Either currentPage or percentComplete is required",
  );

export type UpdateReadingProgressInput = z.infer<
  typeof updateReadingProgressSchema
>;

export const adminCancelRoomSchema = z.object({
  reason: z
    .string()
    .min(1, "Cancellation reason is required")
    .max(500, "Reason must be 500 characters or less"),
});

export type AdminCancelRoomInput = z.infer<typeof adminCancelRoomSchema>;

export const joinRoomSchema = z.object({
  inviteCode: z.string().max(20).optional(),
});
